/**
 * Graviton (ARM) migration recommendations for the Rubicon calculator review.
 *
 * Pure functions — no React, no Cloudscape.
 *
 * WHY THE SAVINGS FIGURE COMES FROM THE BACKEND
 * ---------------------------------------------
 * Detecting an x86 instance and naming its ARM equivalent is entirely
 * client-side. PRICING it is not:
 *
 *   - The frontend never receives a rate table. `ec2_pricing_data` is a
 *     Lambda-local variable and is not serialized into the /process-calculator
 *     response body.
 *   - The public price source the backend uses
 *     (b0.p.awsstatic.com/pricing/2.0/meteredUnitMaps) enforces an ORIGIN
 *     ALLOWLIST. It returns access-control-allow-origin for calculator.aws and
 *     aws.amazon.com, but not for chronos.aws.dev or localhost, so a browser
 *     fetch is blocked by CORS.
 *   - Neither quantity nor utilization is emitted per service, so the source
 *     instance's hourly rate cannot be divided back out of `monthly_cost`
 *     (which also often bundles EBS).
 *
 * So `graviton_annual_savings` is produced server-side, alongside
 * `graviton_recommended_instance_type` and `arm_sp_annual_savings`.
 *
 * WHAT AN ABSENT FIELD MEANS NOW
 * ------------------------------
 * Those fields ship in ChronosLambdas (CR-298688693), so absence no longer means
 * "not implemented". The backend only emits them when it can price the swap, and
 * it resolves the ARM target against the region's own rate table. So a missing
 * figure means NO ARM RATE IS PUBLISHED for that region/OS — a real outcome worth
 * saying out loud. Either way the UI must never render $0 here: $0 is reserved for
 * "we priced it and there is nothing to gain".
 */

/** Sentinel: a recommendation exists but carries no savings figure. */
export const SAVINGS_PENDING = null;

/**
 * Why a recommendation has no dollar figure. Rendered in the savings column in
 * place of a number, so a reader can tell "unpriceable" from "no saving".
 */
export const REASON_STORAGE_IOPS = 'IOPS information needed';
export const REASON_OLA_ASSESSMENT = 'Assessment required';
export const REASON_NO_ARM_RATE = 'No ARM rate published for this region/OS';
export const REASON_NO_UPGRADE_RATE = 'Rate unavailable for the upgraded type';
export const REASON_NO_SAVING = 'No saving at current rates';

/** Coerce a backend numeric field, keeping absent distinct from zero. */
const readNumber = (raw) =>
  raw === undefined || raw === null || Number.isNaN(Number(raw)) ? null : Number(raw);

// There is deliberately NO ARM-target map here any more. Choosing a target requires the
// region's rate table, the OS and the database engine, none of which the frontend has —
// see buildGravitonRecommendation. The mapping lives in ChronosLambdas'
// optimization_utils.ARM_TARGETS_BY_PREFIX, which validates against the real rates.

/**
 * Copied from PricingCalculatorTab's Download Optimization Report helper rather
 * than extracted, so MAP's component is not modified. Config summaries look like
 * "Instance Type (m5.xlarge), Operating system (Linux)".
 */
export const parseConfigSummary = (config, field) => {
  if (!config) return null;
  const match = String(config).match(new RegExp(`${field}\\s*\\(([^)]+)\\)`));
  return match ? match[1] : null;
};

/**
 * Pull an instance type off a service, wherever it lives.
 * EC2 carries `instance_type`; RDS / ElastiCache / OpenSearch express it inside
 * `config_summary`.
 */
export const extractInstanceType = (service) => {
  if (!service) return null;
  if (service.instance_type) return String(service.instance_type);
  const cfg = service.config_summary;
  return (
    parseConfigSummary(cfg, 'Instance Type') ||
    parseConfigSummary(cfg, 'Instance type') ||
    parseConfigSummary(cfg, 'Node Type') ||
    parseConfigSummary(cfg, 'Advance EC2 instance') ||
    null
  );
};

/**
 * Split an instance type into its parts, tolerating the db./cache. prefixes and
 * the .search suffix that RDS, ElastiCache and OpenSearch use.
 *
 * "db.m5.xlarge" -> { qualifier: 'db.', prefix: 'm', generation: 5, spec: '', size: 'xlarge' }
 */
export const parseInstanceType = (instanceType) => {
  if (!instanceType) return null;
  const raw = String(instanceType).trim();

  const qualifierMatch = raw.match(/^(db\.|cache\.)/i);
  const qualifier = qualifierMatch ? qualifierMatch[1] : '';
  let rest = raw.slice(qualifier.length);

  const searchSuffix = /\.search$/i.test(rest) ? '.search' : '';
  if (searchSuffix) rest = rest.slice(0, -searchSuffix.length);

  const parts = rest.split('.');
  if (parts.length < 2) return null;

  const family = parts[0];
  const size = parts.slice(1).join('.');

  // family = letters + digits + optional trailing spec letters, e.g. m6idn, t4g
  const familyMatch = family.match(/^([a-z]+)(\d+)([a-z]*)$/i);
  if (!familyMatch) return null;

  return {
    qualifier,
    searchSuffix,
    prefix: familyMatch[1].toLowerCase(),
    generation: parseInt(familyMatch[2], 10),
    spec: familyMatch[3].toLowerCase(),
    size,
  };
};

/** A family whose spec contains 'g' is already Graviton (m7g, c8g, t4g, x2gd, im4gn). */
export const isGraviton = (instanceType) => {
  const parsed = parseInstanceType(instanceType);
  return parsed ? parsed.spec.includes('g') : false;
};

/**
 * Services that can NEVER run on Graviton, whatever target arrives.
 *
 * Oracle and SQL Server on RDS are x86-only for engine/licensing reasons, and
 * windowsWorkloads is Windows Server + SQL Server on EC2.
 *
 * The backend refuses these too. The duplication is deliberate: the two guards are
 * independent, so neither one being removed — nor the backend's EC2 gate being widened
 * to cover RDS Graviton, which is a planned possibility — can put a licensing-impossible
 * recommendation on a reviewer's screen.
 */
export const GRAVITON_INELIGIBLE_SERVICE_CODES = new Set([
  'amazonRdsForOracle',
  'amazonRdsCustomForOracle',
  'amazonRDSForSQLServer',
  'amazonRdsCustomForSqlServer',
  'windowsWorkloads',
]);

/** True when Graviton can never apply to this service, whatever its instance type. */
export const isGravitonIneligible = (service) =>
  GRAVITON_INELIGIBLE_SERVICE_CODES.has(service?.service_code);

/**
 * Service codes whose Graviton targets the backend validates. EC2 only for now.
 *
 * The backend's Graviton path is gated on `ec2Enhancement`, so no other service can ever
 * receive a target — RDS MySQL/PostgreSQL and ElastiCache do support Graviton, and their
 * savings go unsurfaced until the backend validates those engines. Recorded as a known
 * gap rather than papered over.
 */
const GRAVITON_SCOPED_SERVICE_CODES = new Set(['ec2Enhancement']);

/**
 * True when this row runs an x86 instance we could in principle move to ARM.
 *
 * DETECTION ONLY — it names no target. Classifying a type is safe client-side; choosing
 * a target is not (see buildGravitonRecommendation).
 *
 * Scoped to the services the backend can actually validate, because the UI pairs this
 * count with "no validated ARM target from the backend". Counting an RDS row here would
 * imply a target is merely pending when in fact none is ever coming for that service.
 */
export const isMigratableX86 = (service) => {
  if (!GRAVITON_SCOPED_SERVICE_CODES.has(service?.service_code)) return false;
  if (isGravitonIneligible(service)) return false;
  const instanceType = extractInstanceType(service);
  return !!instanceType && !isGraviton(instanceType);
};

/**
 * Build the Graviton recommendation for one service, or null.
 *
 * THE TARGET MUST COME FROM THE BACKEND (CR-298692617 review). This used to fall back
 * to a client-side family swap, which knows nothing about the region's available types,
 * the operating system, or the database engine — so it offered `db.r7g.*` migrations to
 * RDS for Oracle and SQL Server, which cannot run on Graviton at all, and could name an
 * ARM type absent from the customer's region. Only the backend has the rate table.
 *
 * `annualSavings` is a number when the backend field is present, and null
 * (SAVINGS_PENDING) when it is not. Callers must branch on null rather than
 * coercing it to 0.
 */
export const buildGravitonRecommendation = (service) => {
  // Refused before anything else, and independently of the backend's own refusal.
  if (isGravitonIneligible(service)) return null;

  const currentInstanceType = extractInstanceType(service);
  if (!currentInstanceType) return null;

  const recommendedInstanceType = service.graviton_recommended_instance_type;
  if (!recommendedInstanceType) return null;

  const annualSavings = readNumber(service.graviton_annual_savings) ?? SAVINGS_PENDING;

  return {
    serviceCode: service.service_code || '',
    serviceName: service.service_name || service.service_code || '',
    region: service.region || null,
    currentInstanceType,
    recommendedInstanceType,
    annualSavings,
    /**
     * The backend's exact ARM + commitment figure, or null. Prefer this over
     * composing annualSavings with an x86 Savings Plan saving — see
     * combineComputeSavings for why composition is only a fallback.
     */
    armSpAnnualSavings: readNumber(service.arm_sp_annual_savings),
  };
};

/**
 * Combine two OVERLAPPING compute-rate levers for one service.
 *
 * RI/SP and Graviton are both measured from the same On-Demand x86 baseline:
 *
 *   r = RI/SP saving    = OD - committed_x86
 *   g = Graviton saving = OD - ondemand_arm
 *
 * so r + g counts their intersection twice. The real destination is committed ARM.
 * Writing k = committed_x86 / OD for the commitment discount and assuming it
 * carries over to ARM:
 *
 *   combined = OD - ondemand_arm * k = r + g - (r * g) / OD
 *
 * which is >= max(r, g), <= r + g, and collapses to whichever lever is non-zero.
 *
 * THIS IS A FALLBACK, NOT THE PREFERRED PATH. The assumption above fails when AWS
 * discounts ARM Savings Plans more steeply than x86, in which case the true
 * combined saving EXCEEDS r + g and this understates it. When the backend supplies
 * arm_sp_annual_savings, use that instead — it prices the destination directly.
 * (ChronosLambdas test_steep_arm_discount_exceeds_the_naive_sum documents the case.)
 *
 * Storage and licensing levers are a different cost dimension and stay additive.
 */
export const combineComputeSavings = (riSp, graviton, onDemandAnnual) => {
  const r = Number(riSp) || 0;
  const g = Number(graviton) || 0;
  const od = Number(onDemandAnnual) || 0;
  // With no baseline to divide by, fall back to the larger lever rather than the
  // sum: understating is recoverable, overstating a saving is not.
  if (od <= 0) return Math.max(r, g);
  return r + g - (r * g) / od;
};

/**
 * Is the family-upgrade delta already contained in a bigger lever?
 *
 * `rds_upgrade_annual_savings` prices od(prev) -> od(upgraded). Both of the other
 * compute levers already span that move:
 *   - the RI figure is priced od(prev) -> ri(upgraded), and
 *   - the Graviton figure is priced od(prev) -> od(ARM),
 * each of which changes the instance family as part of the same action. So the
 * upgrade is a FALLBACK lever: it counts only when nothing larger covers it.
 * Adding it on top of either would double-count.
 */
export const upgradeIsCoveredElsewhere = (service, riSpSavings) => {
  if ((Number(riSpSavings) || 0) > 0) return true;
  if (readNumber(service?.arm_sp_annual_savings) !== null) return true;
  const rec = buildGravitonRecommendation(service);
  return !!rec && rec.annualSavings !== SAVINGS_PENDING && rec.annualSavings > 0;
};

/**
 * The compute-rate opportunity for ONE service, overlap already resolved.
 *
 * `riSpSavings` is passed in rather than derived so this stays independent of
 * MAP's getTotalServiceSavings.
 */
export const serviceComputeOpportunity = (service, riSpSavings) => {
  const riSp = Number(riSpSavings) || 0;

  // Fallback lever — see upgradeIsCoveredElsewhere.
  const upgrade = upgradeIsCoveredElsewhere(service, riSp)
    ? 0
    : readNumber(service?.rds_upgrade_annual_savings) || 0;

  // The backend's combined figure wins outright: it needs no composition and
  // carries no assumption about how the commitment discount transfers to ARM.
  const exact = readNumber(service?.arm_sp_annual_savings);
  if (exact !== null) return Math.max(exact, riSp) + upgrade;

  const rec = buildGravitonRecommendation(service);
  const graviton = rec && rec.annualSavings !== SAVINGS_PENDING ? rec.annualSavings : 0;
  const onDemandAnnual =
    (Number(service?.monthly_cost) || 0) * 12 + (Number(service?.upfront_cost) || 0);

  return combineComputeSavings(riSp, graviton, onDemandAnnual) + upgrade;
};

/**
 * Graviton summary across a calculator.
 *
 * `totalSavings` sums only the services that actually carry a figure;
 * `pendingCount` says how many recommendations have no figure yet, so the UI can
 * say so out loud instead of implying the total is complete.
 *
 * `x86Count` is DETECTION, counted independently of any recommendation: rows running a
 * migratable x86 instance, whether or not the backend supplied a target. Since targets
 * are now backend-only, `recommendations` is empty until that ships — and a UI keyed on
 * `recommendations.length` alone would report "no x86 instances detected" for an estimate
 * full of them. These two numbers answer different questions and must stay separate.
 */
export const summarizeGravitonSavings = (services) => {
  const list = services || [];
  const recommendations = list.map(buildGravitonRecommendation).filter(Boolean);

  let totalSavings = 0;
  let pendingCount = 0;
  for (const rec of recommendations) {
    if (rec.annualSavings === SAVINGS_PENDING) pendingCount += 1;
    else totalSavings += rec.annualSavings;
  }

  return {
    recommendations,
    totalSavings,
    pendingCount,
    /** True when at least one recommendation exists but no figure is available. */
    savingsIncomplete: pendingCount > 0,
    x86Count: list.filter(isMigratableX86).length,
  };
};
